/**
 * Shared factory for the three ConversationsPage redux ducks
 * (Creator / Org / Audience). Each page calls
 * `createConversationsDuck(namespace)` and gets back the entire common
 * surface: action types, creators, reducer, thunks, selectors —
 * everything that used to be copy-pasted 3x with only the namespace
 * prefix differing.
 *
 * The page duck file is then a thin wrapper that:
 *   1) calls this factory with its namespace ('CreatorConversationsPage',
 *      'OrgConversationsPage', 'AudienceConversationsPage'),
 *   2) re-exports the factory output,
 *   3) adds page-specific bits (channel grouping, fetchSendbirdToken
 *      with audience-specific avatar lookup, loadData, etc).
 *
 * State shape is identical across pages (extra keys an audience page
 * doesn't use are harmless), so component selectors continue to read
 * `state.CreatorConversationsPage.*`, `state.OrgConversationsPage.*`,
 * etc. as before.
 */

import {
  getChannel,
  archiveChannel as archiveChannelSDK,
  unarchiveChannel as unarchiveChannelSDK,
  deleteChannel as deleteChannelSDK,
  leaveChannel as leaveChannelSDK,
} from '../util/sendbird';
import { sendbirdAPI } from '../util/api';
import { dualOptInAPI, episodeRsvpAPI } from '../api';

const initialState = {
  // Connection
  sendbirdConnected: false,

  // Token
  tokenLoading: false,
  tokenError: null,
  sendbirdToken: null,

  // Channel list
  channels: [],
  channelsLoading: false,
  channelsError: null,
  hasMoreChannels: true,

  // Selected channel
  selectedChannelUrl: null,
  selectedChannel: null,

  // Messages for the selected channel
  messages: [],
  messagesLoading: false,
  messagesError: null,
  hasMoreMessages: true,
  loadingMoreMessages: false,

  // Thread (reply panel)
  activeThreadMessageId: null,
  threadMessages: [],
  threadLoading: false,

  // Channel-action in-flight states
  archiveInProgress: false,
  unarchiveInProgress: false,
  deleteInProgress: false,

  // UI
  channelInfoOpen: false,
  activeTab: 'channels',

  // Channel detail
  channelMembers: [],
  channelBookings: [],

  // Create channel
  createChannelInProgress: false,
  createChannelError: null,

  // Send message
  sendMessageInProgress: false,
  sendMessageError: null,

  // URL routing
  pendingChannelUrl: null,

  // Gig Boost Dual Opt-In — eligibility for the Live Room currently open.
  dualOptIn: null,
  dualOptInSubmitting: false,

  // Episodes of the Live Room currently open, with RSVP state:
  // { counts: { [episodeId]: n }, mine: { [episodeId]: bool }, episodes: [] }
  // `episodes` carries each episode's start/end so the thread can say where
  // it is in its lifecycle without a second round trip.
  episodeRsvps: { counts: {}, mine: {}, episodes: [], listingTitle: null },
};

export const createConversationsDuck = namespace => {
  const t = type => `app/${namespace}/${type}`;

  // ================ Action types ================ //
  const types = {
    SET_SENDBIRD_CONNECTED: t('SET_SENDBIRD_CONNECTED'),
    FETCH_TOKEN_REQUEST: t('FETCH_TOKEN_REQUEST'),
    FETCH_TOKEN_SUCCESS: t('FETCH_TOKEN_SUCCESS'),
    FETCH_TOKEN_ERROR: t('FETCH_TOKEN_ERROR'),

    SET_CHANNELS: t('SET_CHANNELS'),
    ADD_CHANNELS: t('ADD_CHANNELS'),
    ADD_CHANNEL: t('ADD_CHANNEL'),
    UPDATE_CHANNEL: t('UPDATE_CHANNEL'),
    UPDATE_CHANNELS: t('UPDATE_CHANNELS'),
    REMOVE_CHANNEL: t('REMOVE_CHANNEL'),
    REMOVE_CHANNELS: t('REMOVE_CHANNELS'),
    SET_HAS_MORE_CHANNELS: t('SET_HAS_MORE_CHANNELS'),
    SET_SELECTED_CHANNEL: t('SET_SELECTED_CHANNEL'),

    SET_MESSAGES: t('SET_MESSAGES'),
    ADD_MESSAGE: t('ADD_MESSAGE'),
    ADD_MESSAGES: t('ADD_MESSAGES'),
    PREPEND_MESSAGES: t('PREPEND_MESSAGES'),
    UPDATE_MESSAGE: t('UPDATE_MESSAGE'),
    UPDATE_MESSAGES: t('UPDATE_MESSAGES'),
    REMOVE_MESSAGE: t('REMOVE_MESSAGE'),
    REMOVE_MESSAGES: t('REMOVE_MESSAGES'),
    SET_HAS_MORE_MESSAGES: t('SET_HAS_MORE_MESSAGES'),
    SET_LOADING_MORE_MESSAGES: t('SET_LOADING_MORE_MESSAGES'),

    SET_THREAD_MESSAGES: t('SET_THREAD_MESSAGES'),
    ADD_THREAD_MESSAGE: t('ADD_THREAD_MESSAGE'),
    SET_ACTIVE_THREAD: t('SET_ACTIVE_THREAD'),
    SET_THREAD_LOADING: t('SET_THREAD_LOADING'),

    SET_ARCHIVE_IN_PROGRESS: t('SET_ARCHIVE_IN_PROGRESS'),
    SET_UNARCHIVE_IN_PROGRESS: t('SET_UNARCHIVE_IN_PROGRESS'),
    SET_DELETE_IN_PROGRESS: t('SET_DELETE_IN_PROGRESS'),

    SET_CHANNEL_INFO_OPEN: t('SET_CHANNEL_INFO_OPEN'),
    SET_ACTIVE_TAB: t('SET_ACTIVE_TAB'),
    SET_CHANNEL_MEMBERS: t('SET_CHANNEL_MEMBERS'),
    SET_CHANNEL_BOOKINGS: t('SET_CHANNEL_BOOKINGS'),
    SET_PENDING_CHANNEL_URL: t('SET_PENDING_CHANNEL_URL'),

    FETCH_CHANNELS_REQUEST: t('FETCH_CHANNELS_REQUEST'),
    FETCH_CHANNELS_SUCCESS: t('FETCH_CHANNELS_SUCCESS'),
    FETCH_CHANNELS_ERROR: t('FETCH_CHANNELS_ERROR'),

    FETCH_MESSAGES_REQUEST: t('FETCH_MESSAGES_REQUEST'),
    FETCH_MESSAGES_SUCCESS: t('FETCH_MESSAGES_SUCCESS'),
    FETCH_MESSAGES_ERROR: t('FETCH_MESSAGES_ERROR'),

    SEND_MESSAGE_REQUEST: t('SEND_MESSAGE_REQUEST'),
    SEND_MESSAGE_SUCCESS: t('SEND_MESSAGE_SUCCESS'),
    SEND_MESSAGE_ERROR: t('SEND_MESSAGE_ERROR'),

    CREATE_CHANNEL_REQUEST: t('CREATE_CHANNEL_REQUEST'),
    CREATE_CHANNEL_SUCCESS: t('CREATE_CHANNEL_SUCCESS'),
    CREATE_CHANNEL_ERROR: t('CREATE_CHANNEL_ERROR'),

    SET_EPISODE_RSVPS: t('SET_EPISODE_RSVPS'),
    SET_EPISODE_RSVP: t('SET_EPISODE_RSVP'),

    SET_DUAL_OPT_IN: t('SET_DUAL_OPT_IN'),
    DUAL_OPT_IN_SUBMIT_REQUEST: t('DUAL_OPT_IN_SUBMIT_REQUEST'),
    DUAL_OPT_IN_SUBMIT_DONE: t('DUAL_OPT_IN_SUBMIT_DONE'),
  };

  // ================ Reducer ================ //
  const reducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
      case types.SET_SENDBIRD_CONNECTED:
        return { ...state, sendbirdConnected: payload };

      case types.FETCH_TOKEN_REQUEST:
        return { ...state, tokenLoading: true, tokenError: null };
      case types.FETCH_TOKEN_SUCCESS:
        return { ...state, tokenLoading: false, sendbirdToken: payload };
      case types.FETCH_TOKEN_ERROR:
        return { ...state, tokenLoading: false, tokenError: payload };

      case types.SET_CHANNELS:
        return { ...state, channels: payload };

      case types.ADD_CHANNELS:
        return {
          ...state,
          channels: [
            ...state.channels,
            ...payload.filter(
              c => !state.channels.find(e => e.channelUrl === c.channelUrl)
            ),
          ],
        };

      case types.ADD_CHANNEL:
        return {
          ...state,
          channels: [
            payload,
            ...state.channels.filter(c => c.channelUrl !== payload.channelUrl),
          ],
        };

      case types.UPDATE_CHANNEL: {
        // Also refresh channelMembers when the updated channel is the
        // selected one, so the sidebar roster reflects adds/removes.
        const isSelected = state.selectedChannel?.channelUrl === payload.channelUrl;
        return {
          ...state,
          channels: state.channels.map(c =>
            c.channelUrl === payload.channelUrl ? { ...c, ...payload } : c
          ),
          selectedChannel: isSelected
            ? { ...state.selectedChannel, ...payload }
            : state.selectedChannel,
          channelMembers:
            isSelected && payload.members ? payload.members : state.channelMembers,
        };
      }

      case types.UPDATE_CHANNELS: {
        const updatedForSelected = state.selectedChannel
          ? payload.find(u => u.channelUrl === state.selectedChannel.channelUrl)
          : null;
        return {
          ...state,
          channels: state.channels.map(c => {
            const updated = payload.find(u => u.channelUrl === c.channelUrl);
            return updated ? { ...c, ...updated } : c;
          }),
          selectedChannel: updatedForSelected
            ? { ...state.selectedChannel, ...updatedForSelected }
            : state.selectedChannel,
          channelMembers: updatedForSelected?.members
            ? updatedForSelected.members
            : state.channelMembers,
        };
      }

      case types.REMOVE_CHANNEL:
        return {
          ...state,
          channels: state.channels.filter(c => c.channelUrl !== payload),
          selectedChannelUrl:
            state.selectedChannelUrl === payload ? null : state.selectedChannelUrl,
          selectedChannel:
            state.selectedChannel?.channelUrl === payload ? null : state.selectedChannel,
        };

      case types.REMOVE_CHANNELS:
        return {
          ...state,
          channels: state.channels.filter(c => !payload.includes(c.channelUrl)),
          selectedChannelUrl: payload.includes(state.selectedChannelUrl)
            ? null
            : state.selectedChannelUrl,
          selectedChannel:
            state.selectedChannel && payload.includes(state.selectedChannel.channelUrl)
              ? null
              : state.selectedChannel,
        };

      case types.SET_HAS_MORE_CHANNELS:
        return { ...state, hasMoreChannels: payload };

      case types.SET_SELECTED_CHANNEL: {
        // Only reset the message/thread state when we're actually switching to
        // a different channel. Re-selecting the same channel (e.g. upgrading a
        // plain Platform-API channel to its live SDK instance) must preserve
        // the messages that were just loaded — clearing them here on the
        // upgrade was wiping the whole conversation.
        const isSameChannel =
          !!payload?.channelUrl && payload.channelUrl === state.selectedChannelUrl;
        return {
          ...state,
          selectedChannelUrl: payload?.channelUrl || null,
          selectedChannel: payload,
          messages: isSameChannel ? state.messages : [],
          hasMoreMessages: isSameChannel ? state.hasMoreMessages : true,
          activeThreadMessageId: isSameChannel ? state.activeThreadMessageId : null,
          threadMessages: isSameChannel ? state.threadMessages : [],
        };
      }

      case types.SET_MESSAGES:
        return { ...state, messages: payload };

      case types.ADD_MESSAGE:
        if (state.messages.find(m => m.messageId === payload.messageId)) {
          return state;
        }
        return { ...state, messages: [...state.messages, payload] };

      case types.ADD_MESSAGES: {
        const newMsgs = payload.filter(
          m => !state.messages.find(e => e.messageId === m.messageId)
        );
        return { ...state, messages: [...state.messages, ...newMsgs] };
      }

      case types.PREPEND_MESSAGES: {
        const olderMsgs = payload.filter(
          m => !state.messages.find(e => e.messageId === m.messageId)
        );
        return { ...state, messages: [...olderMsgs, ...state.messages] };
      }

      case types.UPDATE_MESSAGE:
        return {
          ...state,
          messages: state.messages.map(m =>
            m.messageId === payload.messageId ? { ...m, ...payload } : m
          ),
        };

      case types.UPDATE_MESSAGES:
        return {
          ...state,
          messages: state.messages.map(m => {
            const updated = payload.find(u => u.messageId === m.messageId);
            return updated ? { ...m, ...updated } : m;
          }),
        };

      case types.REMOVE_MESSAGE:
        return {
          ...state,
          messages: state.messages.filter(m => m.messageId !== payload),
        };

      case types.REMOVE_MESSAGES:
        return {
          ...state,
          messages: state.messages.filter(m => !payload.includes(m.messageId)),
        };

      case types.SET_HAS_MORE_MESSAGES:
        return { ...state, hasMoreMessages: payload };

      case types.SET_LOADING_MORE_MESSAGES:
        return { ...state, loadingMoreMessages: payload };

      case types.SET_THREAD_MESSAGES:
        return { ...state, threadMessages: payload };

      case types.ADD_THREAD_MESSAGE:
        if (state.threadMessages.find(m => m.messageId === payload.messageId)) {
          return state;
        }
        return { ...state, threadMessages: [...state.threadMessages, payload] };

      case types.SET_ACTIVE_THREAD:
        return {
          ...state,
          activeThreadMessageId: payload,
          threadMessages: payload ? state.threadMessages : [],
        };

      case types.SET_THREAD_LOADING:
        return { ...state, threadLoading: payload };

      case types.SET_ARCHIVE_IN_PROGRESS:
        return { ...state, archiveInProgress: payload };
      case types.SET_UNARCHIVE_IN_PROGRESS:
        return { ...state, unarchiveInProgress: payload };
      case types.SET_DELETE_IN_PROGRESS:
        return { ...state, deleteInProgress: payload };

      case types.SET_EPISODE_RSVPS:
        return { ...state, episodeRsvps: payload };

      case types.SET_EPISODE_RSVP: {
        // Optimistic single-episode update so the button responds instantly.
        const { episodeId, rsvped, count } = payload;
        return {
          ...state,
          episodeRsvps: {
            ...state.episodeRsvps,
            counts: { ...state.episodeRsvps.counts, [episodeId]: count },
            mine: { ...state.episodeRsvps.mine, [episodeId]: rsvped },
          },
        };
      }

      case types.SET_DUAL_OPT_IN:
        return { ...state, dualOptIn: payload };

      case types.DUAL_OPT_IN_SUBMIT_REQUEST:
        return { ...state, dualOptInSubmitting: true };

      case types.DUAL_OPT_IN_SUBMIT_DONE: {
        // Accepting makes the whole thing moot. Declining stops the automatic
        // ask but must leave the manual path open, so the record is kept with
        // eligibility cleared rather than dropped.
        const accepted = payload?.decision === 'accepted';
        const keepManual = !accepted && state.dualOptIn?.manualAvailable;
        return {
          ...state,
          dualOptInSubmitting: false,
          dualOptIn: keepManual
            ? { ...state.dualOptIn, eligible: false, lastDecisionAt: new Date().toISOString() }
            : null,
        };
      }

      case types.SET_CHANNEL_INFO_OPEN:
        return { ...state, channelInfoOpen: payload };
      case types.SET_ACTIVE_TAB:
        return { ...state, activeTab: payload };
      case types.SET_CHANNEL_MEMBERS:
        return { ...state, channelMembers: payload };
      case types.SET_CHANNEL_BOOKINGS:
        return { ...state, channelBookings: payload };
      case types.SET_PENDING_CHANNEL_URL:
        return { ...state, pendingChannelUrl: payload };

      case types.FETCH_CHANNELS_REQUEST:
        return { ...state, channelsLoading: true, channelsError: null };
      case types.FETCH_CHANNELS_SUCCESS:
        return { ...state, channelsLoading: false, channels: payload };
      case types.FETCH_CHANNELS_ERROR:
        return { ...state, channelsLoading: false, channelsError: payload };

      case types.FETCH_MESSAGES_REQUEST:
        return { ...state, messagesLoading: true, messagesError: null };
      case types.FETCH_MESSAGES_SUCCESS:
        return { ...state, messagesLoading: false, messages: payload };
      case types.FETCH_MESSAGES_ERROR:
        return { ...state, messagesLoading: false, messagesError: payload };

      case types.SEND_MESSAGE_REQUEST:
        return { ...state, sendMessageInProgress: true, sendMessageError: null };
      case types.SEND_MESSAGE_SUCCESS:
        return { ...state, sendMessageInProgress: false };
      case types.SEND_MESSAGE_ERROR:
        return {
          ...state,
          sendMessageInProgress: false,
          sendMessageError: payload,
        };

      case types.CREATE_CHANNEL_REQUEST:
        return {
          ...state,
          createChannelInProgress: true,
          createChannelError: null,
        };
      case types.CREATE_CHANNEL_SUCCESS:
        // Sendbird returns the EXISTING channel when isDistinct=true
        // already has a channel for the same user-set — dedupe so the
        // list doesn't render duplicate rows with the same key.
        return {
          ...state,
          createChannelInProgress: false,
          channels: [
            payload,
            ...state.channels.filter(c => c.channelUrl !== payload.channelUrl),
          ],
        };
      case types.CREATE_CHANNEL_ERROR:
        return {
          ...state,
          createChannelInProgress: false,
          createChannelError: payload,
        };

      default:
        return state;
    }
  };

  // ================ Action creators ================ //
  const actions = {
    setSendbirdConnected: payload => ({ type: types.SET_SENDBIRD_CONNECTED, payload }),

    setChannels: payload => ({ type: types.SET_CHANNELS, payload }),
    addChannels: payload => ({ type: types.ADD_CHANNELS, payload }),
    addChannel: payload => ({ type: types.ADD_CHANNEL, payload }),
    updateChannel: payload => ({ type: types.UPDATE_CHANNEL, payload }),
    updateChannels: payload => ({ type: types.UPDATE_CHANNELS, payload }),
    removeChannel: payload => ({ type: types.REMOVE_CHANNEL, payload }),
    removeChannels: payload => ({ type: types.REMOVE_CHANNELS, payload }),
    setHasMoreChannels: payload => ({ type: types.SET_HAS_MORE_CHANNELS, payload }),
    setSelectedChannel: payload => ({ type: types.SET_SELECTED_CHANNEL, payload }),

    setMessages: payload => ({ type: types.SET_MESSAGES, payload }),
    addMessage: payload => ({ type: types.ADD_MESSAGE, payload }),
    addMessages: payload => ({ type: types.ADD_MESSAGES, payload }),
    prependMessages: payload => ({ type: types.PREPEND_MESSAGES, payload }),
    updateMessage: payload => ({ type: types.UPDATE_MESSAGE, payload }),
    updateMessages: payload => ({ type: types.UPDATE_MESSAGES, payload }),
    removeMessage: payload => ({ type: types.REMOVE_MESSAGE, payload }),
    removeMessages: payload => ({ type: types.REMOVE_MESSAGES, payload }),
    setHasMoreMessages: payload => ({ type: types.SET_HAS_MORE_MESSAGES, payload }),
    setLoadingMoreMessages: payload => ({
      type: types.SET_LOADING_MORE_MESSAGES,
      payload,
    }),

    setThreadMessages: payload => ({ type: types.SET_THREAD_MESSAGES, payload }),
    addThreadMessage: payload => ({ type: types.ADD_THREAD_MESSAGE, payload }),
    setActiveThread: payload => ({ type: types.SET_ACTIVE_THREAD, payload }),
    setThreadLoading: payload => ({ type: types.SET_THREAD_LOADING, payload }),

    setArchiveInProgress: payload => ({
      type: types.SET_ARCHIVE_IN_PROGRESS,
      payload,
    }),
    setUnarchiveInProgress: payload => ({
      type: types.SET_UNARCHIVE_IN_PROGRESS,
      payload,
    }),
    setDeleteInProgress: payload => ({ type: types.SET_DELETE_IN_PROGRESS, payload }),

    setChannelInfoOpen: payload => ({ type: types.SET_CHANNEL_INFO_OPEN, payload }),
    setActiveTab: payload => ({ type: types.SET_ACTIVE_TAB, payload }),
    setChannelMembers: payload => ({ type: types.SET_CHANNEL_MEMBERS, payload }),
    setChannelBookings: payload => ({ type: types.SET_CHANNEL_BOOKINGS, payload }),
    setPendingChannelUrl: payload => ({
      type: types.SET_PENDING_CHANNEL_URL,
      payload,
    }),
  };

  // ================ Thunks ================ //

  const fetchChannelByUrl = channelUrl => async (dispatch, getState) => {
    try {
      const channels = getState()[namespace]?.channels || [];
      const existing = channels.find(c => c.channelUrl === channelUrl);
      if (existing) return existing;

      const channel = await getChannel(channelUrl);
      if (channel) {
        dispatch(actions.addChannel(channel));
        return channel;
      }
      return null;
    } catch (error) {
      console.error('Error fetching channel by URL:', error);
      return null;
    }
  };

  const fetchChannels = (customTypes = null) => async dispatch => {
    dispatch({ type: types.FETCH_CHANNELS_REQUEST });
    try {
      const params = customTypes ? { customTypes: customTypes.join(',') } : {};
      const response = await sendbirdAPI.getChannels(params);
      const channels = response.data?.channels || [];
      dispatch({ type: types.FETCH_CHANNELS_SUCCESS, payload: channels });
      return channels;
    } catch (error) {
      console.error('Fetch channels error:', error);
      dispatch({ type: types.FETCH_CHANNELS_ERROR, payload: error.message });
      throw error;
    }
  };

  const createChannel = channelData => async dispatch => {
    dispatch({ type: types.CREATE_CHANNEL_REQUEST });
    try {
      const channel = await sendbirdAPI.createChannel(channelData);
      dispatch({ type: types.CREATE_CHANNEL_SUCCESS, payload: channel });
      return channel;
    } catch (error) {
      console.error('Create channel error:', error);
      dispatch({ type: types.CREATE_CHANNEL_ERROR, payload: error.message });
      throw error;
    }
  };

  const archiveChannel = channelUrl => async dispatch => {
    try {
      const { channel, adminMessage } = await archiveChannelSDK(
        channelUrl,
        sendbirdAPI.sendAdminMessage
      );
      dispatch(actions.updateChannel(channel));
      if (adminMessage) dispatch(actions.addMessage(adminMessage));
      return channel;
    } catch (error) {
      console.error('Archive channel error:', error);
      throw error;
    }
  };

  const unarchiveChannel = channelUrl => async dispatch => {
    try {
      const { channel, adminMessage } = await unarchiveChannelSDK(
        channelUrl,
        sendbirdAPI.sendAdminMessage
      );
      dispatch(actions.updateChannel(channel));
      if (adminMessage) dispatch(actions.addMessage(adminMessage));
      return channel;
    } catch (error) {
      console.error('Unarchive channel error:', error);
      throw error;
    }
  };

  const deleteChannel = channelUrl => async dispatch => {
    try {
      await deleteChannelSDK(channelUrl);
      dispatch(actions.removeChannel(channelUrl));
    } catch (error) {
      console.error('Delete channel error:', error);
      throw error;
    }
  };

  const leaveChannel = channelUrl => async dispatch => {
    try {
      await leaveChannelSDK(channelUrl);
      dispatch(actions.removeChannel(channelUrl));
    } catch (error) {
      console.error('Leave channel error:', error);
      throw error;
    }
  };

  const addChannelMembers = (channelUrl, userIds) => async dispatch => {
    try {
      const result = await sendbirdAPI.addMembers(channelUrl, userIds);
      dispatch(actions.setChannelMembers(result.members));
      return result;
    } catch (error) {
      console.error('Add members error:', error);
      throw error;
    }
  };

  const removeChannelMember = (channelUrl, memberId) => async (
    dispatch,
    getState
  ) => {
    try {
      await sendbirdAPI.removeMember(channelUrl, memberId);
      const channelMembers = getState()[namespace]?.channelMembers || [];
      dispatch(
        actions.setChannelMembers(channelMembers.filter(m => m.userId !== memberId))
      );
    } catch (error) {
      console.error('Remove member error:', error);
      throw error;
    }
  };

  /**
   * Gig Boost Dual Opt-In eligibility for a Live Room the member has open.
   *
   * Best-effort by design: this is an optional invitation, so a failure means
   * "don't offer it", never a visible error in the middle of a conversation.
   */
  const fetchDualOptInStatus = listingId => async dispatch => {
    if (!listingId) {
      dispatch({ type: types.SET_DUAL_OPT_IN, payload: null });
      return null;
    }
    try {
      const res = await dualOptInAPI.status(listingId);
      const body = res?.data ?? res ?? {};
      const status = body?.data ?? body;
      // Keep the record when either the automatic prompt applies or the
      // member can still opt in manually — the manual door outlives the ask.
      const payload =
        status?.eligible || status?.manualAvailable ? { ...status, listingId } : null;
      dispatch({ type: types.SET_DUAL_OPT_IN, payload });
      return payload;
    } catch (e) {
      dispatch({ type: types.SET_DUAL_OPT_IN, payload: null });
      return null;
    }
  };

  /**
   * Record the member's answer. `consentText` is the exact sentence they were
   * shown, stored verbatim server-side so a later copy edit cannot rewrite
   * what was agreed to.
   */
  const submitDualOptInDecision = (listingId, { decision, consentText }) => async dispatch => {
    dispatch({ type: types.DUAL_OPT_IN_SUBMIT_REQUEST });
    try {
      await dualOptInAPI.decide(listingId, {
        decision,
        consentText,
        locale: typeof document !== 'undefined' ? document.documentElement.lang : undefined,
      });
    } catch (e) {
      // The prompt is cleared either way — re-asking after a failed answer
      // would be worse than silently missing one opt-in.
      console.error('Dual opt-in decision failed:', e?.message || e);
    } finally {
      dispatch({ type: types.DUAL_OPT_IN_SUBMIT_DONE, payload: { decision } });
    }
  };

  /**
   * RSVP counts for a Live Room, plus which episodes this member reserved.
   * Best-effort: an episode card without counts is still usable.
   */
  const fetchEpisodeRsvps = listingId => async dispatch => {
    if (!listingId) {
      dispatch({ type: types.SET_EPISODE_RSVPS, payload: { counts: {}, mine: {}, episodes: [], listingTitle: null } });
      return;
    }
    try {
      const res = await episodeRsvpAPI.summary(listingId);
      const body = res?.data ?? res ?? {};
      const data = body?.data ?? body;
      dispatch({
        type: types.SET_EPISODE_RSVPS,
        payload: {
          counts: data?.counts || {},
          mine: data?.mine || {},
          episodes: data?.episodes || [],
          listingTitle: data?.listingTitle || null,
        },
      });
    } catch (e) {
      dispatch({
        type: types.SET_EPISODE_RSVPS,
        payload: { counts: {}, mine: {}, episodes: [], listingTitle: null },
      });
    }
  };

  /**
   * Reserve or release a spot. Applied optimistically and reconciled with the
   * server's answer, so the button never feels laggy but can't drift either.
   */
  const toggleEpisodeRsvp = (listingId, episodeId) => async (dispatch, getState) => {
    const before = getState()[namespace]?.episodeRsvps || { counts: {}, mine: {} };
    const wasMine = !!before.mine[episodeId];
    const prevCount = before.counts[episodeId] || 0;

    dispatch({
      type: types.SET_EPISODE_RSVP,
      payload: {
        episodeId,
        rsvped: !wasMine,
        count: Math.max(0, prevCount + (wasMine ? -1 : 1)),
      },
    });

    try {
      const res = await episodeRsvpAPI.toggle(listingId, episodeId);
      const body = res?.data ?? res ?? {};
      const data = body?.data ?? body;
      dispatch({
        type: types.SET_EPISODE_RSVP,
        payload: { episodeId, rsvped: !!data?.rsvped, count: data?.count ?? prevCount },
      });
    } catch (e) {
      // Roll back to what the server last told us.
      dispatch({
        type: types.SET_EPISODE_RSVP,
        payload: { episodeId, rsvped: wasMine, count: prevCount },
      });
    }
  };

  const thunks = {
    fetchEpisodeRsvps,
    toggleEpisodeRsvp,
    fetchDualOptInStatus,
    submitDualOptInDecision,
    fetchChannelByUrl,
    fetchChannels,
    createChannel,
    archiveChannel,
    unarchiveChannel,
    deleteChannel,
    leaveChannel,
    addChannelMembers,
    removeChannelMember,
  };

  // ================ Selectors ================ //
  const pick = key => state => state[namespace]?.[key];

  const selectors = {
    selectSendbirdConnected: pick('sendbirdConnected'),
    selectTokenLoading: pick('tokenLoading'),
    selectTokenError: pick('tokenError'),
    selectSendbirdToken: pick('sendbirdToken'),
    selectChannels: pick('channels'),
    selectHasMoreChannels: pick('hasMoreChannels'),
    selectSelectedChannel: pick('selectedChannel'),
    selectMessages: pick('messages'),
    selectMessagesLoading: pick('messagesLoading'),
    selectHasMoreMessages: pick('hasMoreMessages'),
    selectLoadingMoreMessages: pick('loadingMoreMessages'),
    selectActiveTab: pick('activeTab'),
    selectChannelInfoOpen: pick('channelInfoOpen'),
    selectActiveThreadMessageId: pick('activeThreadMessageId'),
    selectThreadMessages: pick('threadMessages'),
    selectThreadLoading: pick('threadLoading'),
    selectArchiveInProgress: pick('archiveInProgress'),
    selectUnarchiveInProgress: pick('unarchiveInProgress'),
    selectDeleteInProgress: pick('deleteInProgress'),
    selectChannelMembers: pick('channelMembers'),
    selectChannelBookings: pick('channelBookings'),
    selectPendingChannelUrl: pick('pendingChannelUrl'),
    selectEpisodeRsvps: pick('episodeRsvps'),
    selectDualOptIn: pick('dualOptIn'),
    selectDualOptInSubmitting: pick('dualOptInSubmitting'),

    selectTotalUnreadCount: state => {
      const channels = state[namespace]?.channels || [];
      return channels.reduce(
        (total, channel) => total + (channel.unreadMessageCount || 0),
        0
      );
    },
  };

  return { types, actions, reducer, thunks, selectors, initialState };
};
